import React, { FC, useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Modal } from "@pd/pd-ui-kit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ZDEditorForm from "~/components/Forms/ZDEditorForm";
import EditRow from "~/components/Icons/svg/EditRow";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getFormatDate } from "~/libs/getFormatDate";
import { TABLE_REDUCER_OPTIONS } from "~/pages/Editor/EditorServiceAuthorization/TableReducer/options";
import {
  useSetRenameGroupToolsInsuranceMutation,
  useSetRenameGroupToolsMutation,
  ZdGroupType,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { ConfirmModal } from "~/shared/ui/ConfirmModal";
import { Field } from "~/shared/ui/Field";
import { TableV2 } from "~/shared/ui/TableV2";

import { getEditorZDColumns } from "./model/tableAdapter";

import s from "./style.less";
import tableV2Styles from "~/shared/ui/TableV2/ui/TableV2.less";

const ZD_EDITOR_FORM_ID = "ZDEditorForm";
const ZD_EDIT_NAME_FORM_ID = "ZDEditGroupNameForm";

function injectGroupHeaderRows<
  T extends { isOneParrent?: boolean; GroupOne?: string; id?: string | number },
>(
  rows: T[] | undefined,
): T[] | Array<T | { id: string; __isGroupHeader: true; GroupOne: string }> {
  if (!rows?.length) {
    return rows as T[];
  }
  const out: Array<
    T | { id: string; __isGroupHeader: true; GroupOne: string }
  > = [];
  rows.forEach((row) => {
    if (row.isOneParrent) {
      out.push({
        id: `zd-group-${String(row.GroupOne)}-${row.id}`,
        __isGroupHeader: true,
        GroupOne: row.GroupOne as string,
      });
    }
    out.push(row);
  });
  return out;
}

interface ITableFactory {
  selectedService;
  periodModel;
  options;
  items;
  loading;
  toolsInsuranceParentWrite;
  toolsParentWrite;
  arrToolsInsuranceGroup;
  arrToolsGroup;
  refetchToolsGroup;
  refetchToolsInsuranceGroup;
  refetchParrentTools;
  refetchParrentToolsInsurance;
  refetch;
  create;
  change;
  blind;
  deleteMut;
  selectPlaces?;
}
const TableReducer: FC<ITableFactory> = ({
  selectedService,
  periodModel,
  options,
  items,
  loading,
  toolsInsuranceParentWrite,
  refetchToolsInsuranceGroup,
  refetchToolsGroup,
  toolsParentWrite,
  arrToolsInsuranceGroup,
  arrToolsGroup,
  refetchParrentTools,
  refetchParrentToolsInsurance,
  refetch,
  create,
  change,
  blind,
  deleteMut,
  selectPlaces,
}) => {
  const { t } = useTranslation();
  const language = localStorage.getItem("language");
  const isEn = language === "en";
  const { hasPermission } = usePermissions();
  const [nameGroup, setNameGroup] = useState("");

  const setData = useSetLocationData();
  const { openModal, currentId } = useGetLocationData();

  const handleCloseModal = () => {
    setData("openModal", "", "currentId", "");
  };
  const arrSelectTrade = ["places", "tradePremises"];

  const reconstructData = items.reduce((accFirst, el) => {
    accFirst["forTable"] = accFirst["forTable"] || [];
    accFirst["forEdit"] = accFirst["forEdit"] || [];

    accFirst["forTable"] = [
      ...accFirst["forTable"],
      Object.keys(el).reduce((acc, key) => {
        if (typeof el[key] === "object" && !!el[key] && key !== "ZD_PlacesId") {
          acc[key + "Id"] = el[key].id;
          acc[key + "IdPeriod"] = el[key].period;
          return acc;
        }
        if (key === "ZD_PlacesId") {
          const lab = selectPlaces.find((post) => post.value === el[key]);
          if (lab) acc[key] = lab.label;
          return acc;
        }
        // for date view
        if (key === "ActualFrom") {
          acc[key] = getFormatDate(new Date(el[key]), true);
          return acc;
        }
        // for Name En
        if (key === "Name") {
          acc[key] = isEn ? el["NameEn"] : el[key];
          return acc;
        }
        if (arrSelectTrade.includes(selectedService) && key === "GroupOne") {
          acc[key] = t(`editorZD.${el[key]}`);
          return acc;
        }
        if (key === "groupArea") {
          acc[key] = t(`editorZD.${el[key]}`);
          return acc;
        }
        if (key === "NameEn") {
          return acc;
        }
        acc[key] = el[key];
        return acc;
      }, {}),
    ];

    accFirst["forEdit"] = [
      ...accFirst["forEdit"],
      Object.keys(el).reduce((acc, key) => {
        acc[key] = acc[key] || {};
        if (typeof el[key] === "object" && !!el[key] && key !== "ZD_PlacesId") {
          acc[key + "Id"] = isEn
            ? { label: `(${el[key].id}) ${el[key].nameEn}`, value: el[key].id }
            : { label: `(${el[key].id}) ${el[key].nameRu}`, value: el[key].id };
          return acc;
        }
        if (typeof el[key] === "object" && !!el[key] && key === "ZD_PlacesId") {
          acc[key] = { label: `${el[key].value}`, value: el[key].id };
          return acc;
        }
        if (key === "ZD_PlacesId") {
          const lab = selectPlaces.find((post) => post.value === el[key]);
          if (lab) acc[key] = { label: `${lab.label}`, value: lab.value };
          return acc;
        }
        if (selectedService === "tradePremises" && key === "GroupOne") {
          acc[key] = { label: t(`editorZD.${el[key]}`), value: el[key] };
          return acc;
        }
        if (selectedService === "places" && key === "GroupOne") {
          acc[key] = { label: t(`editorZD.${el[key]}`), value: el[key] };
          return acc;
        }
        if (key === "NameEn") {
          acc[key] = el[key];
          return acc;
        }
        acc[key] = el[key];
        return acc;
      }, {}),
    ];

    return accFirst;
  }, {});

  const isAdd = openModal === "Add";
  const isEdit = openModal === "Edit";
  const selectedField =
    currentId &&
    reconstructData.forEdit &&
    reconstructData.forEdit.filter((el) => el.id === currentId)[0];
  const schema = yup.object().shape({
    groupNew: yup
      .string()
      .required(t("validation.required"))
      .nullable()
      .max(250, t("validation.more250")),
  });

  const { handleSubmit, control, setValue, watch, errors } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (openModal === "EditName") {
      setValue("groupNew", nameGroup);
    }
  }, [openModal, nameGroup, setValue]);

  const [deleteMutation] = deleteMut({
    variables: { id: String(currentId) },
    onCompleted() {
      refetch();
    },
  });

  const { notifyError } = useNotify();

  const handleMutationCompleted = () => {
    refetch();
    refetchParrentTools();
    refetchParrentToolsInsurance();
    refetchToolsGroup();
    refetchToolsInsuranceGroup();
    handleCloseModal();
  };

  const handleMutationError = ({ graphQLErrors }) => {
    notifyError(graphQLErrors[0].message);
  };

  const [setCreateToolsMutation, createToolsMutation] = create({
    onCompleted: handleMutationCompleted,
    onError: handleMutationError,
  });

  const [setBlindChangeToolsMutation, blindChangeToolsMutation] = blind({
    onCompleted: handleMutationCompleted,
    onError: handleMutationError,
  });

  const [setChangeToolsMutation, changeToolsMutation] = change({
    onCompleted: handleMutationCompleted,
    onError: handleMutationError,
  });

  const [checkBtn, setCheckBtn] = useState(false);

  const handleFormSubmit = (baseParams) => {
    if (openModal === "Edit") {
      if (!checkBtn) {
        setChangeToolsMutation({
          variables: {
            id: currentId,
            ...baseParams,
          },
        });
      } else {
        setBlindChangeToolsMutation({
          variables: {
            id: currentId,
            ...baseParams,
          },
        });
      }
    } else {
      setCreateToolsMutation({
        variables: {
          ...baseParams,
        },
      });
    }
  };
  const [setRenameGroupToolsInsurance] =
    useSetRenameGroupToolsInsuranceMutation({
      onCompleted() {
        refetch();
      },
    });
  const [setRenameGroupTools] = useSetRenameGroupToolsMutation({
    onCompleted() {
      refetch();
    },
  });

  const deleteIndicator = () => {
    deleteMutation();
    handleCloseModal();
  };
  const onSumbitRename = (data) => {
    if (selectedService === "tools") {
      setRenameGroupTools({
        variables: {
          oldGroup: nameGroup,
          groupType: ZdGroupType.GroupOne,
          newGroup: data.groupNew,
        },
      });
    } else {
      setRenameGroupToolsInsurance({
        variables: {
          oldGroup: nameGroup,
          groupType: ZdGroupType.GroupOne,
          newGroup: data.groupNew,
        },
      });
    }
    handleCloseModal();
  };

  const arrGroup = [];

  const isArrData =
    (selectedService === "tools" || selectedService === "toolsInsurance") &&
    reconstructData.forTable &&
    reconstructData.forTable.map((el) => {
      if (arrGroup.includes(el.GroupOne)) {
        return {
          ...el,
          isOneParrent: false,
        };
      } else {
        arrGroup.push(el.GroupOne);
        return {
          ...el,
          isOneParrent: true,
        };
      }
    });

  const selectParent =
    (selectedService === "tools" || selectedService === "toolsInsurance") &&
    isArrData &&
    isArrData.map((el) => ({
      value: el.UUID,
      label: el.Name,
      GroupOne: el.GroupOne,
      id: el.id,
    }));

  const selectParentSelect =
    (selectedService === "tools" &&
      toolsParentWrite.length > 0 &&
      toolsParentWrite.map((el) => ({
        value: el.UUID,
        label: el.Name,
        GroupOne: el.GroupOne,
        id: el.id,
      }))) ||
    (selectedService === "toolsInsurance" &&
      toolsInsuranceParentWrite.length > 0 &&
      toolsInsuranceParentWrite.map((el) => ({
        value: el.UUID,
        label: el.Name,
        isParent: el.GroupOne,
        id: el.id,
      })));

  const columns = getEditorZDColumns(t, options, selectedService, periodModel);

  const handleRowEdit = (record: {
    id?: string | number;
    __isGroupHeader?: boolean;
  }) => {
    if (record.__isGroupHeader) return;
    setData("openModal", "Edit", "currentId", record.id);
  };

  const handleRowDelete = (record: {
    id?: string | number;
    __isGroupHeader?: boolean;
  }) => {
    if (record.__isGroupHeader) return;
    setData("openModal", "Sure", "currentId", record.id);
  };

  const baseTableRows = isArrData ? isArrData : reconstructData.forTable;
  const dataSource =
    selectedService === "tools" || selectedService === "toolsInsurance"
      ? injectGroupHeaderRows(baseTableRows)
      : baseTableRows;

  const isGroupRow = useCallback((record: { __isGroupHeader?: boolean }) => {
    return record.__isGroupHeader === true;
  }, []);

  const renderGroupRow = useCallback(
    (record: { GroupOne?: string }) => (
      <div className={s.zdGroupRowInner}>
        <div
          className={tableV2Styles.rowControlBtn}
          title={t("ui.btn_edit")}
          onClick={(e) => {
            e.stopPropagation();
            setNameGroup(record.GroupOne || "");
            setData("openModal", "EditName");
          }}
        >
          <EditRow />
        </div>
        <span>{record.GroupOne}</span>
      </div>
    ),
    [t, setData],
  );

  const rowDecoratorForGroups = useCallback(
    (record: { __isGroupHeader?: boolean }) =>
      record.__isGroupHeader ? { className: s.zdGroupRow } : {},
    [],
  );

  return (
    <>
      <TableV2
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        onRowEdit={hasPermission("zd.edit") ? handleRowEdit : undefined}
        onRowDelete={hasPermission("zd.delete") ? handleRowDelete : undefined}
        {...(selectedService === "tools" || selectedService === "toolsInsurance"
          ? {
              isGroupRow,
              renderGroupRow,
              rowDecorator: rowDecoratorForGroups,
            }
          : {})}
      />
      <Modal
        open={isAdd || isEdit}
        title={t("editorGR.add_gr_indicators")}
        onCancel={handleCloseModal}
        centered
        closable
        destroyOnHidden
        footer={[
          <Button
            key="saveHistory"
            htmlType="submit"
            form={ZD_EDITOR_FORM_ID}
            onClick={() => setCheckBtn(false)}
            loading={changeToolsMutation.loading}
            color={"secondary"}
          >
            {t("ui.save_history")}
          </Button>,
          <Button
            key="save"
            htmlType="submit"
            form={ZD_EDITOR_FORM_ID}
            onClick={() => setCheckBtn(true)}
            loading={
              createToolsMutation.loading || blindChangeToolsMutation.loading
            }
          >
            {t("ui.save")}
          </Button>,
        ]}
      >
        <ZDEditorForm
          formId={ZD_EDITOR_FORM_ID}
          onSubmit={handleFormSubmit}
          selectedService={selectedService}
          isEditable={true}
          data={selectedField}
          selectPlaces={selectPlaces}
          arrToolsGroup={arrToolsGroup}
          arrToolsInsuranceGroup={arrToolsInsuranceGroup}
          selectParent={selectParentSelect}
          options={TABLE_REDUCER_OPTIONS[selectedService]}
        />
      </Modal>

      <ConfirmModal
        open={openModal === "Sure"}
        text={`${t("zd.deleteZd")}?`}
        onConfirm={deleteIndicator}
        onCancel={handleCloseModal}
        centered
        closable
      />
      <Modal
        open={openModal === "EditName"}
        title={`${t("zd.editName")}`}
        onCancel={handleCloseModal}
        centered
        closable
        destroyOnHidden
        cancelButton={{ children: t("ui.cancel") }}
        okButton={{
          htmlType: "submit",
          form: ZD_EDIT_NAME_FORM_ID,
          children: t("ui.save"),
        }}
      >
        <form id={ZD_EDIT_NAME_FORM_ID} onSubmit={handleSubmit(onSumbitRename)}>
          <Field
            type="input"
            label={t("zd.editNameInput")}
            error={errors["groupNew"]?.message}
            name="groupNew"
            control={control}
            defaultValue={nameGroup}
          />
        </form>
      </Modal>
    </>
  );
};
export default TableReducer;
